/**
 * Navigation assembly for the app shells.
 *
 * Kept as pure functions, separate from the shells that render them, for two
 * reasons: the shells no longer decide who sees what (page grants do), and the
 * filtering is the part worth testing — `vitest.config.ts` runs in `node` with
 * no DOM, so a pure function is the only form a unit test can reach.
 *
 * Labels and icons stay with the shells, which own the i18n keys and the icon
 * imports; these functions only decide which entries survive and where the
 * dividers fall.
 */

import type { ReactNode } from 'react';
import type { PageKey } from '../router/pageRegistry';

export type NavEntry = {
  page: PageKey;
  text: string;
  icon: ReactNode;
  path: string;
};

/** One page reached from a group's row, shown as a tab in the secondary bar. */
export type NavTab = {
  page: PageKey;
  text: string;
  path: string;
};

/**
 * A sidebar row standing for several pages.
 *
 * `key` rather than a page key of its own: a group grants nothing, so there is
 * nothing to hold or revoke at this level. Which tabs a reader sees is decided
 * entirely by the page grants they already have.
 */
export type NavGroup = {
  key: string;
  text: string;
  icon: ReactNode;
  tabs: NavTab[];
};

export type NavItem = NavEntry | NavGroup | { divider: true };

export const isDivider = (item: NavItem): item is { divider: true } =>
  'divider' in item;

export const isGroup = (item: NavItem): item is NavGroup =>
  !('divider' in item) && 'tabs' in item;

/**
 * One sidebar row, before it has a name.
 *
 * The shells add the label and the icon; this module decides only what rows
 * there are, in what order, and which pages are tabs of which. Splitting it
 * there is what lets the assembly be unit-tested at all — `vitest.config.ts`
 * runs in `node`, so anything holding an icon is out of reach.
 */
export type NavRow =
  | {
      kind: 'page';
      page: PageKey;
      path: string;
      section: string;
      /**
       * Set when this row is a group that collapsed to its last held tab. The
       * row shows the page's name under the group's icon, which is what it did
       * before the grouping moved into the database: the icon says which part of
       * the app you are in, and one tab does not change that.
       */
      fromGroup?: string;
    }
  | {
      kind: 'group';
      group: string;
      section: string;
      tabs: { page: PageKey; path: string }[];
    };

export type NavRowItem = NavRow | { divider: true };

export const isRowDivider = (row: NavRowItem): row is { divider: true } =>
  'divider' in row;

/** What the server says about one page's place in the navigation. */
export type LayoutPage = {
  pageKey: PageKey;
  routePath: string;
  navSection: string;
  displayOrder: number;
  navGroupKey: string | null;
};

/** What the server says about one sidebar row standing for several pages. */
export type LayoutGroup = {
  groupKey: string;
  navSection: string;
  displayOrder: number;
};

/**
 * Assemble the sidebar from what the server said, in one pass.
 *
 * The shells used to hold this as three literal arrays, which meant the
 * grouping was written down twice — once here and once, in a different shape,
 * in the screen that hands out pages. It is one table now (V57) and this reads
 * it.
 *
 * Four rules, all of which existed before and none of which the server decides:
 *
 * - A group keeps only the tabs the reader was granted. Nothing filters on a
 *   key of the group's own, because a group has no key to grant.
 * - A group down to **one** tab stops being a group and renders as that page's
 *   own row, under that page's own label. A group name over a single screen,
 *   with no strip and nothing to explain it, is worse than the plain row.
 * - A group down to **none** leaves no trace: not an empty row, and not a
 *   stranded divider.
 * - Dividers fall where the section changes, never first and never twice.
 *
 * `allowedPages` is applied again here even though the server already filtered
 * `layout` by it. The two answers come from one request so they cannot
 * disagree, but the guard reads `allowedPages` and the sidebar reads this, and
 * a row the router would bounce is worse than a row that is missing.
 */
export const buildNavRows = (
  allowedPages: ReadonlySet<PageKey>,
  groups: readonly LayoutGroup[],
  layout: readonly LayoutPage[],
): NavRowItem[] => {
  const pages = layout
    .filter((page) => allowedPages.has(page.pageKey))
    .slice()
    .sort((left, right) => left.displayOrder - right.displayOrder);

  // Keyed on the groups that were actually sent, so a page naming a group that
  // was not — dissolved, or outside the reader's sections — falls through to a
  // row of its own rather than into a bucket nothing ever reads.
  const known = new Set(groups.map((group) => group.groupKey));

  const byGroup = new Map<string, LayoutPage[]>();
  for (const page of pages) {
    if (page.navGroupKey === null || !known.has(page.navGroupKey)) continue;
    const held = byGroup.get(page.navGroupKey);
    if (held === undefined) byGroup.set(page.navGroupKey, [page]);
    else held.push(page);
  }

  const rows: (NavRow & { order: number })[] = [];

  for (const page of pages) {
    // A page whose group was never sent — dissolved, or outside the reader's
    // sections — is a row of its own, which is what it was before the group.
    if (page.navGroupKey !== null && byGroup.has(page.navGroupKey)) continue;
    rows.push({
      kind: 'page',
      page: page.pageKey,
      path: page.routePath,
      section: page.navSection,
      order: page.displayOrder,
    });
  }

  for (const group of groups) {
    const tabs = byGroup.get(group.groupKey) ?? [];
    if (tabs.length === 0) continue;

    if (tabs.length === 1) {
      const [only] = tabs;
      rows.push({
        kind: 'page',
        page: only.pageKey,
        path: only.routePath,
        fromGroup: group.groupKey,
        // The group's slot, not the page's: a collapsed group must not jump up
        // the sidebar because the one tab it kept happens to sort early, nor
        // cross a divider because that tab is a page of another section. That
        // is 'profile', a member page, alone in the account section's row.
        section: group.navSection,
        order: group.displayOrder,
      });
      continue;
    }

    rows.push({
      kind: 'group',
      group: group.groupKey,
      section: group.navSection,
      order: group.displayOrder,
      tabs: tabs.map((tab) => ({ page: tab.pageKey, path: tab.routePath })),
    });
  }

  rows.sort((left, right) => left.order - right.order);

  return withDividers(rows.map(({ order: _order, ...item }) => item));
};

/** Dividers fall where the section changes, never first and never twice. */
const withDividers = (rows: readonly NavRow[]): NavRowItem[] =>
  rows.flatMap((row, index) => {
    const previous = rows[index - 1];
    return previous !== undefined && previous.section !== row.section
      ? [{ divider: true as const }, row]
      : [row];
  });

/**
 * The two planes the sidebar can show, by `nav_section`.
 *
 * Mirrors `Api/model/app_page.py:57-60`, which is where the split is decided.
 * The API serves the union of both to somebody holding both — that is the whole
 * point of `getUserSections` — and the shell shows one at a time, because a
 * sidebar carrying the platform's rows next to an organisation's invites the
 * reading that the platform row acts on the organisation below it. It does not:
 * ADR 0027 made those two different jobs, and this is that decision in the
 * chrome.
 */
export const GLOBAL_PLANE_SECTIONS: readonly string[] = ['admin'];

export type Plane = 'global' | 'organisation';

export const planeOfSection = (section: string): Plane =>
  GLOBAL_PLANE_SECTIONS.includes(section) ? 'global' : 'organisation';

/**
 * Which plane an address is in.
 *
 * Read from the URL rather than held as state, so the two can never disagree.
 * A bookmark into `/admin/roles` lands with the platform rows already showing,
 * and the back button moves the sidebar with it.
 */
export const planeForPath = (pathname: string, adminPrefix: string): Plane =>
  matchesPath(pathname, adminPrefix) ? 'global' : 'organisation';

/** The rows of one plane, with the dividers redrawn for what is left. */
export const rowsInPlane = (
  rows: readonly NavRowItem[],
  plane: Plane,
): NavRowItem[] =>
  withDividers(
    rows.filter(
      (row): row is NavRow =>
        !isRowDivider(row) && planeOfSection(row.section) === plane,
    ),
  );

/**
 * Where switching to a plane lands: its first row, or its first tab.
 *
 * Null when the reader holds nothing there, which is what tells the switcher
 * not to offer the plane at all.
 */
export const firstPathInPlane = (
  rows: readonly NavRowItem[],
  plane: Plane,
): string | null => {
  const row = rows.find(
    (item): item is NavRow =>
      !isRowDivider(item) && planeOfSection(item.section) === plane,
  );
  if (row === undefined) return null;
  return row.kind === 'page' ? row.path : (row.tabs[0]?.path ?? null);
};

/**
 * Does `pathname` fall inside `path`?
 *
 * Stricter than the shells' own `pathname.startsWith(path)`, which the grouped
 * navigation cannot reuse: it has to distinguish the member's `/programmes`
 * from the organisation's `/tenant/programmes`, and it must not let
 * `/tenant/programmes` claim `/tenant/programme-measurements`. A child route
 * still matches its parent, because those deliberately carry the parent's page
 * key — `/documents/7/review` is part of Documents rather than a page of its own.
 */
export const matchesPath = (pathname: string, path: string): boolean =>
  path === '/'
    ? pathname === '/'
    : pathname === path || pathname.startsWith(`${path}/`);

/**
 * Which tab of a group the current address is on, by longest match.
 *
 * Longest rather than first, because a tab keeps its own child routes: a screen
 * that opens an editor at `/x/y/edit/1` is still that screen.
 *
 * Until V60 the rule was load-bearing for a worse reason — a group's first tab
 * sat at the group's own address, so its path was a prefix of all four
 * siblings' and first match would have highlighted it everywhere. No shipped
 * group nests like that now, but the paths come from the database, so a build
 * meeting a schema still on V59 is answered with exactly that arrangement. The
 * rule is what keeps the right tab lit through it.
 */
export const activeTabFor = (
  pathname: string,
  tabs: readonly NavTab[],
): NavTab | null =>
  tabs
    .filter((tab) => matchesPath(pathname, tab.path))
    .reduce<NavTab | null>(
      (best, tab) => (best === null || tab.path.length > best.path.length ? tab : best),
      null,
    );

/** Is this row the one the reader is on — for a group, any of its tabs. */
export const isRowActive = (pathname: string, item: NavEntry | NavGroup): boolean =>
  isGroup(item)
    ? item.tabs.some((tab) => matchesPath(pathname, tab.path))
    : matchesPath(pathname, item.path);

/**
 * The address a group's own row points at: the first tab its reader holds.
 *
 * Per-user by construction, which is the point — a group whose first tab was
 * revoked must open on the next one rather than bounce off a guard. Every tab
 * keeps its own permanent route, so links and bookmarks are unaffected.
 */
export const groupLandingPath = (group: NavGroup): string | null =>
  group.tabs[0]?.path ?? null;

/**
 * Where a group's own address opens, for a reader holding these pages.
 *
 * The answer `groupLandingPath` gives the sidebar row, read from the layout
 * rather than from an assembled row: the router needs it before there are rows,
 * to resolve '/coaches' and '/admin/master-data' to a screen.
 *
 * Null when the reader holds nothing in the group, or when nothing said how the
 * pages are arranged. Guessing a page is the caller's business, not this one's.
 */
export const groupLandingFor = (
  groupKey: string,
  allowedPages: ReadonlySet<PageKey>,
  layout: readonly LayoutPage[],
): string | null =>
  layout
    .filter(
      (page) => page.navGroupKey === groupKey && allowedPages.has(page.pageKey),
    )
    .reduce<LayoutPage | null>(
      (first, page) =>
        first === null || page.displayOrder < first.displayOrder ? page : first,
      null,
    )?.routePath ?? null;

/**
 * The path to send someone to when they ask for a page they may not see.
 *
 * Prefers the first entry of the visible navigation so the redirect lands
 * somewhere meaningful rather than on a fixed page they might also lack.
 * `fallback` is used when nothing at all is visible, which in a seeded
 * database cannot happen — the core pages are always granted.
 */
export const firstVisiblePath = (items: NavItem[], fallback: string): string => {
  const firstRow = items.find(
    (item): item is NavEntry | NavGroup => !isDivider(item),
  );
  if (firstRow === undefined) return fallback;
  // A group has no path of its own; the row stands for its first held tab.
  return (isGroup(firstRow) ? groupLandingPath(firstRow) : firstRow.path) ?? fallback;
};
